import type { Zone } from '@seek/audience-zones';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { setProfileSuggestionsExperiment } from 'src/components/Profile/actionCreators';
import { useCandidateId } from 'src/components/Profile/data';
import { useAppConfig } from 'src/modules/appConfig';
import type { Maybe, Page } from 'src/types';

import { useTypedSelector } from '../hooks/useTypedSelector';

import { isFeatureActive } from '.';

export type ExperimentVariant = '0' | '1' | '2';

const EXPERIMENT_VARIANT_CONTROL_GROUP: ExperimentVariant = '0';
const EXPERIMENT_VARIANT_TEST_GROUP: ExperimentVariant = '1';
const EXPERIMENT_VARIANT_OTHER: ExperimentVariant = '2';
// This value is staying in place as this code is currently being used to show
// a modal to 50% of candidates
const AB_TEST_ROLLOUT_PERCENTAGE = 50;

const abTestRolloutPercentageComparator = (percentage: number) =>
  percentage * 10;

const abTestGroupSplittingPoint = abTestRolloutPercentageComparator(
  AB_TEST_ROLLOUT_PERCENTAGE,
);

const last3DigitsOfCandidateId = (candidateId: string) =>
  parseInt(candidateId, 10) % 1000;

const allowList: string[] = ['568466545']; // Add candidate Ids in to include in test group (sshaikh+test4@seekasia.com)

const isInAllowList = (candidateId: string) => allowList.includes(candidateId);

// When the last 3 digits of candidate ID fall below the percentage*10 (ie, < 500 if 50%)
const inTestGroup = (candidateId: string): boolean => {
  const isInTestGroup =
    last3DigitsOfCandidateId(candidateId) < abTestGroupSplittingPoint;

  return isInTestGroup || isInAllowList(candidateId);
};

// When the last 3 digits of candidate ID fall between the percentage*10 ~ percentage*10*2 (ie, 500 - 999 if 50%)
const inControlGroup = (candidateId: string): boolean => {
  const isInControlGroup =
    last3DigitsOfCandidateId(candidateId) >= abTestGroupSplittingPoint &&
    last3DigitsOfCandidateId(candidateId) < abTestGroupSplittingPoint * 2 &&
    !isInAllowList(candidateId);

  return isInControlGroup;
};

// 50% rollout: 50% - 0 (control) and 50% - 1 (test variant)
export const getProfileSuggestionsExperimentVariant = ({
  zone,
  page,
  candidateId,
}: {
  zone: Zone;
  page: Page;
  candidateId: Maybe<string>;
}): ExperimentVariant | undefined => {
  const isProfileAndANZ =
    page === 'profile-page' && (zone === 'anz-1' || zone === 'anz-2');

  const isProfileInsightsAbTestFeatureFlagActive = isFeatureActive(
    'profile-suggestions',
  );

  if (isProfileAndANZ && candidateId) {
    if (inTestGroup(candidateId) || isProfileInsightsAbTestFeatureFlagActive) {
      return EXPERIMENT_VARIANT_TEST_GROUP;
    }

    if (inControlGroup(candidateId)) {
      return EXPERIMENT_VARIANT_CONTROL_GROUP;
    }

    return EXPERIMENT_VARIANT_OTHER;
  }
};

export const useProfileSuggestionsAbTest = () => {
  const page = useTypedSelector((store) => store.router.page);
  const dispatch = useDispatch();
  const { zone } = useAppConfig();
  const { candidateId } = useCandidateId();

  const experimentVariant = getProfileSuggestionsExperimentVariant({
    zone,
    page,
    candidateId,
  });

  const isInProfileSuggestionsTestGroup =
    experimentVariant === EXPERIMENT_VARIANT_TEST_GROUP;
  const isInProfileSuggestionsControlGroup =
    experimentVariant === EXPERIMENT_VARIANT_CONTROL_GROUP; // This group will be shown for Profile Strength
  useEffect(() => {
    dispatch(setProfileSuggestionsExperiment(isInProfileSuggestionsTestGroup));
  }, [dispatch, isInProfileSuggestionsTestGroup]);
  return {
    isInProfileSuggestionsTestGroup,
    isInProfileSuggestionsControlGroup,
  };
};
